import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getAttribution, track } from "@/lib/tracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || localStorage.getItem("avb_ref") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: params.get("role") === "owner" ? "owner" : "customer" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register({ ...form, referred_by: refCode, acquisition: getAttribution() });
    setLoading(false);
    if (res.ok) {
      track(res.user.role === "owner" ? "owner_signup" : "referral_signup", { referred_by: refCode });
      toast.success("Account created!");
      navigate(res.user.role === "owner" ? "/owner" : "/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md animate-fade-up rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
        <Link to="/" className="inline-block" data-testid="app-logo">
          <p className="font-heading text-3xl font-extrabold tracking-tight text-primary">Avinya <span className="font-semibold text-stone-800">Bookings</span></p>
          <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.32em] text-amber-600">Book · Relax · Celebrate</p>
        </Link>
        <h1 className="mt-6 font-heading text-2xl font-semibold text-stone-900">Create your account</h1>

        <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-stone-100 p-1">
          {[
            { v: "customer", l: "I'm a Customer" },
            { v: "owner", l: "I'm an Owner" },
          ].map((r) => (
            <button
              key={r.v}
              type="button"
              data-testid={`role-${r.v}`}
              onClick={() => setForm({ ...form, role: r.v })}
              className={`rounded-lg py-2 text-sm font-medium transition-colors ${form.role === r.v ? "bg-white text-primary shadow-sm" : "text-stone-500"}`}
            >
              {r.l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input id="name" data-testid="reg-name" required value={form.name} onChange={set("name")} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="phone">Mobile Number</Label>
            <Input id="phone" data-testid="reg-phone" required value={form.phone} onChange={set("phone")} className="mt-1 rounded-xl" placeholder="9876543210" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1 rounded-xl" placeholder="min 6 characters" />
          </div>
          {error && <p data-testid="reg-error" className="text-sm text-destructive">{error}</p>}
          <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-primary py-6 text-base hover:bg-green-900">
            {loading ? "Creating..." : "Create Account"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-stone-500">
          Already have an account? <Link to="/login" data-testid="go-login" className="font-semibold text-primary hover:underline">Login</Link>
        </p>
      </div>
    </div>
  );
}
